import {
  type Step as RcStep,
  StoredConditions,
} from '@sb/remote-control/types';

import type { Diagnostic, RoutineView, ValidationRule } from '../../types';
import {
  catchActionRequiredToDiagnostic,
  createStepRule,
  diag,
  type Warn,
} from '../../util/ruleHelpers';
import {
  validateConditional,
  validateStoredConditionShape,
} from '../../util/validators';

/**
 * Validate the Antigravity step's condition, if present.
 *
 * System A checks:
 * 1. Standard conditional validation (same as If/ElseIf)
 * 2. IO-only constraint: only `ioState` variable kinds are allowed
 */
function validateAntigravityCondition(
  routine: RoutineView,
  stepId: string,
  warn: Warn,
): Diagnostic[] {
  const stepCfg = routine.stepConfigurations[stepId];
  if (!stepCfg) return [];

  const { args }: { args: RcStep.Arguments } = stepCfg;
  if (args.argumentKind !== 'Antigravity' || !args.condition) return [];
  let parsed: unknown;

  try {
    parsed = JSON.parse(args.condition || '[]');
  } catch (error: unknown) {
    return [
      diag({
        code: 'ui.invalidConfiguration',
        message: 'Condition is not valid JSON.',
        stepId,
        sourceRule: 'steps.Antigravity',
        fieldId: 'condition',
      }),
    ];
  }

  const diagnostics: Diagnostic[] = [];

  let conditions: StoredConditions;

  try {
    validateStoredConditionShape(parsed);
    conditions = StoredConditions.parse(parsed);
    warn(validateConditional(conditions, routine));
  } catch (error: unknown) {
    return catchActionRequiredToDiagnostic(error, stepId, 'steps.Antigravity');
  }

  // 2. IO-only constraint: only ioState variable kinds are allowed
  const violatesIoOnly = conditions.some((orGroup) =>
    orGroup.some(
      (condition) =>
        condition.variable && condition.variable.kind !== 'ioState',
    ),
  );

  if (violatesIoOnly) {
    diagnostics.push(
      diag({
        code: 'ui.invalidConfiguration',
        message:
          'Only IO conditions are allowed for the alternative end condition.',
        stepId,
        sourceRule: 'steps.Antigravity',
        fieldId: 'condition',
      }),
    );
  }

  return diagnostics;
}

export const AntigravityValidation: ValidationRule = createStepRule({
  id: 'steps.Antigravity',
  stepKinds: ['Antigravity'],
  run: ({ routine, stepId, warn }) =>
    validateAntigravityCondition(routine, stepId, warn),
});
